import React from 'react';
import { AnimatePresence } from 'framer-motion';
import { FiBell, FiX, FiCheck, FiAlertCircle } from 'react-icons/fi';
import useNotificationPanel from './useNotificationPanel';
import { getNotificationIcon, getNotificationIconClass, formatTimeAgo } from './notificationPanelHelpers';
import {
  NotificationPanel, PanelHeader, PanelTitle, MarkAllButton, CloseButton, NotificationList,
  NotificationItem, NotificationIcon, NotificationContent, NotificationTitle,
  NotificationMessage, NotificationTime, NotificationActions, ActionButton, EmptyState,
  LoadingState, UnreadIndicator
} from './NotificationPanel.styles';

function NotificationPanelComponent({ isOpen, onClose }) {
  const {
    notifications, loading, error, markAsRead, markAllAsRead, unreadCount
  } = useNotificationPanel({ isOpen });

  return (
    <AnimatePresence>
      {isOpen && (
        <NotificationPanel
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          data-notification-panel
        >
          <PanelHeader>
            <PanelTitle>
              <FiBell size={16} />
              Notifications
              {unreadCount > 0 && (
                <span style={{
                  background: 'var(--primary-color)',
                  color: 'var(--text-inverse)',
                  fontSize: '0.75rem',
                  padding: '0.125rem 0.375rem',
                  borderRadius: 0,
                  marginLeft: '0.5rem'
                }}>
                  {unreadCount}
                </span>
              )}
            </PanelTitle>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {unreadCount > 0 && (
                <MarkAllButton onClick={markAllAsRead}>
                  <FiCheck size={14} />
                  Mark all read
                </MarkAllButton>
              )}
              <CloseButton onClick={onClose}>
                <FiX size={16} />
              </CloseButton>
            </div>
          </PanelHeader>

          <NotificationList>
            {loading ? (
              <LoadingState>
                <p>Loading notifications...</p>
              </LoadingState>
            ) : error ? (
              <EmptyState>
                <FiAlertCircle />
                <p>Failed to load notifications</p>
              </EmptyState>
            ) : notifications.length === 0 ? (
              <EmptyState>
                <FiBell />
                <p>No notifications yet</p>
              </EmptyState>
            ) : (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  className={!notification.is_read ? 'unread' : ''}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {!notification.is_read && <UnreadIndicator />}

                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <NotificationIcon className={getNotificationIconClass(notification.type)}>
                      {getNotificationIcon(notification.type)}
                    </NotificationIcon>

                    <NotificationContent>
                      <NotificationTitle>{notification.title}</NotificationTitle>
                      <NotificationMessage>{notification.message}</NotificationMessage>
                      <NotificationTime>{formatTimeAgo(notification.created_at)}</NotificationTime>

                      {!notification.is_read && (
                        <NotificationActions>
                          <ActionButton
                            className="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                          >
                            <FiCheck size={12} />
                            Mark as read
                          </ActionButton>
                        </NotificationActions>
                      )}
                    </NotificationContent>
                  </div>
                </NotificationItem>
              ))
            )}
          </NotificationList>
        </NotificationPanel>
      )}
    </AnimatePresence>
  );
}

export default NotificationPanelComponent;
